import React, { memo, useCallback, useState } from 'react';
import { Box, SimpleGrid } from '@chakra-ui/react';
import TicTacToeCell from './TicTacToeCell';

/**
 * @param {object} props
 * @param {import('../utils/gameLogic').Board} props.board
 * @param {number[] | null} props.winningLine
 * @param {boolean} props.gameOver
 * @param {'X' | 'O'} props.currentPlayer
 * @param {(index: number) => void} props.onMove
 */
function TicTacToeBoard({ board, winningLine, gameOver, currentPlayer, onMove }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const winningSet = winningLine ? new Set(winningLine) : null;

  const handleHover = useCallback((i) => {
    setHoverIndex(i);
  }, []);

  return (
    <Box
      role="application"
      aria-label="Tic tac toe board"
      w="100%"
      maxW={{ base: 'min(100%, 400px)', sm: '440px' }}
      mx="auto"
      p={{ base: 5, md: 6 }}
      borderRadius="2xl"
      bg="linear-gradient(168deg, rgba(30, 41, 59, 0.97) 0%, rgba(15, 23, 42, 0.99) 55%, rgba(15, 23, 42, 1) 100%)"
      borderWidth="1px"
      borderColor="whiteAlpha.120"
      boxShadow="
        0 0 0 1px rgba(255,255,255,0.06) inset,
        0 2px 6px rgba(0,0,0,0.06),
        0 22px 44px -18px rgba(15, 23, 42, 0.5),
        0 0 72px -28px rgba(45, 212, 191, 0.1)
      "
      sx={{
        '@keyframes ttt-board-in': {
          '0%': { opacity: 0, transform: 'translateY(8px) scale(0.99)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        animation: 'ttt-board-in 0.34s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      }}
    >
      <SimpleGrid columns={3} spacing={{ base: 3.5, md: 4 }}>
        {board.map((cell, index) => {
          const isWinning = winningSet?.has(index) ?? false;
          const showGhost =
            !gameOver &&
            cell === null &&
            hoverIndex === index &&
            currentPlayer !== undefined;
          return (
            <TicTacToeCell
              key={index}
              index={index}
              value={cell}
              isWinning={isWinning}
              disabled={gameOver || cell !== null}
              showGhost={showGhost}
              ghostPlayer={currentPlayer}
              isActivePlayerCell={!gameOver && hoverIndex === index && cell === null}
              onCellClick={onMove}
              onHoverChange={handleHover}
            />
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

export default memo(TicTacToeBoard);
